import { Box, Text, Flex } from '@everlywell/ui-kit';
import { PlayCircle } from '@phosphor-icons/react';
import React from 'react';

interface MediaComponentProps {
  url: string;
  videoThumb?: string;
  title?: string;
  videoText?: string;
  disclaimer?: string;
  subtitles?: string;
}

export const MediaComponent: React.FC<MediaComponentProps> = ({
  url,
  videoThumb,
  videoText,
  disclaimer,
  subtitles,
}) => {
  const [isPlaying, setIsPlaying] = React.useState(false);
  const videoRef = React.useRef<HTMLVideoElement>(null);

  const handleVideoClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
        setIsPlaying(false);
      } else {
        videoRef.current.play();
        setIsPlaying(true);
      }
    }
  };

  const handleVideoEnded = () => {
    setIsPlaying(false);
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleVideoClick(e as unknown as React.MouseEvent);
    }
  };

  return (
    <Box
      position="relative"
      paddingTop="56.25%"
      onClick={handleVideoClick}
      cursor={isPlaying ? 'pointer' : 'default'}
      role="region"
      aria-label="Video player"
    >
      <Box
        as="video"
        poster={videoThumb}
        borderRadius="lg"
        ref={videoRef}
        position="absolute"
        top={0}
        left={0}
        width="100%"
        height="100%"
        objectFit="cover"
        src={url}
        title="PDP Video"
        aria-label="PDP Video"
        crossOrigin="anonymous"
        onEnded={handleVideoEnded}
      >
        {subtitles && isPlaying && (
          <track
            default
            kind="subtitles"
            srcLang="en"
            src={subtitles}
            label="English"
          />
        )}
      </Box>
      <Box
        position="absolute"
        borderRadius="lg"
        top={0}
        left={0}
        right={0}
        bottom={0}
        background="linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.2) 100%)"
        zIndex={1}
        opacity={isPlaying ? 0 : 1}
        transition="opacity 0.2s"
      />
      <Flex
        position="absolute"
        bottom={0}
        left={0}
        w="100%"
        h="fit-content"
        opacity={isPlaying ? 0 : 1}
        zIndex={4}
        flexDirection="row"
        alignItems="center"
        justifyContent="center"
        textAlign="center"
        px={6}
        py={4}
        gap={2}
      >
        <Box
          as="button"
          aria-label={isPlaying ? 'Pause video' : 'Play video'}
          onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
            e.stopPropagation();
            handleVideoClick(e);
          }}
          onKeyDown={handleKeyDown}
          tabIndex={0}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <PlayCircle
            size={52}
            color="white"
            weight="fill"
            style={{
              opacity: 0.9,
            }}
            className="play-circle"
          />
        </Box>
        <Box maxWidth="400px" mx="auto" mb={1}>
          <Text
            color="white"
            fontSize={['12px', '14px', '14px']}
            fontWeight="medium"
          >
            {videoText}
          </Text>
          <Text color="#A9A9A9" fontSize="10px" fontWeight="medium">
            {disclaimer}
          </Text>
        </Box>
      </Flex>
    </Box>
  );
};
